import { apiSlice } from '@/features/api/apiSlice';

export type DailyBucket = {
  date: string;
  delivered: number;
  opened: number;
  clicked: number;
  bounced: number;
};

export type StatsAggregate = {
  delivered: number;
  opened: number;
  clicked: number;
  bounced: number;
  deferred: number;
  complained: number;
  sent: number;
  failed: number;
  openRate: number;
  clickRate: number;
  bounceRate: number;
};

export type TopTemplate = {
  templateId: string;
  name: string;
  slug: string;
  sends: number;
  opens: number;
  clicks: number;
  openRate: number;
  clickRate: number;
};

export type ProviderHealthRow = {
  providerConnectionId: string | null;
  total: number;
  failed: number;
  successRate: number;
  p50LatencyMs: number | null;
};

export type CohortRetentionRow = {
  week: string;
  recipients: number;
  retention1d: number;
  retention7d: number;
  retention30d: number;
};

export type AnalyticsDashboard = {
  range: { since30: string; since7: string };
  totals30: StatsAggregate;
  totals7: StatsAggregate;
  byDay: DailyBucket[];
  topTemplates: TopTemplate[];
  providerHealth: ProviderHealthRow[];
  cohortRetention: CohortRetentionRow[];
  deliverabilityScore: number;
};

export type LatencyPercentiles = {
  p50: number | null;
  p95: number | null;
  p99: number | null;
  sampleSize: number;
};

export type ProviderErrorRow = {
  providerConnectionId: string;
  bounced: number;
  complained: number;
  failed: number;
  deferred: number;
};

export type BounceTaxonomy = {
  hard: number;
  soft: number;
  spam_complaint: number;
  blocklist: number;
  unknown: number;
};

export type RetryRecoveryStats = {
  firstAttemptFailures: number;
  recovered: number;
  recoveryRate: number | null;
};

export type DeferredPatterns = {
  currentlyDeferred: number;
  avgAgeSeconds: number | null;
};

export type ProviderUptimeRow = {
  providerConnectionId: string;
  activeHours: number;
  degradedHours: number;
  uptime: number | null;
};

export type DeliveryHealth = {
  windowDays: number;
  latency: LatencyPercentiles;
  providerErrors: ProviderErrorRow[];
  bounceTaxonomy: BounceTaxonomy;
  retryRecovery: RetryRecoveryStats;
  deferred: DeferredPatterns;
  uptime: ProviderUptimeRow[];
};

export const analyticsApi = apiSlice.injectEndpoints({
  endpoints: (b) => ({
    dashboard: b.query<AnalyticsDashboard, void>({
      query: () => '/analytics/dashboard',
      providesTags: ['Send'],
    }),
    deliveryHealth: b.query<DeliveryHealth, void>({
      query: () => '/analytics/delivery-health',
      providesTags: ['Send'],
    }),
  }),
});

export const { useDashboardQuery, useDeliveryHealthQuery } = analyticsApi;
